<template>
  <div class="w-full" id="WorkAndFinances">
    <ValidationObserver ref="WorkAndFinances" v-slot="observer">
      <h2 class="mb-5 text-2xl font-bold text-center lg:text-4xl">Work and finances</h2>
      <div class="mt-5 mb-6">
        
        <!-- Employment Status -->
        <AggregatorEditRow :complete="isEmploymentStatusComplete" title="Employment status" :preview="`${prettifyEmploymentStatus(employmentStatusCode)}`">
            <ValidationProvider name="Employment Status" ref="employmentStatus" rules="required">
              <div slot-scope="{ errors }">
                <p class="font-semibold label">Employment Status<TickIcon class="tick-icon"/></p>
                <fieldset>
                  <div class="inline" v-for="employmentStatusCodeValue in employmentStatusCodes" :key="employmentStatusCodeValue">
                    <input name="EM" v-model="employmentStatusCode" type="radio" :id="employmentStatusCodeValue" :value="employmentStatusCodeValue" />
                    <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" :for="employmentStatusCodeValue">
                      {{ prettifyEmploymentStatus(employmentStatusCodeValue) }}
                      <TickIcon class="tick-icon"/>
                    </label>
                </div>
                </fieldset>
                <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
        </AggregatorEditRow>
        
        <!-- Occupation -->
        <AggregatorEditRow :required="occupation.length > 0" :complete="isOccupationFieldComplete && occupation.length > 0" title="Occupation" :preview="`${occupation}`">
            <label class="font-semibold label" for="occupation">Occupation</label>
            <ValidationProvider ref="occupation" name="Occupation" :rules="occupationRules">
              <div slot-scope="{ errors }">
                <input tabindex="0" :class="{ 'border-error': errors[0] }" v-model="occupation" class="input focus:outline-none" id="occupation" type="text" autocomplete="off">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
        </AggregatorEditRow>
        
        <!-- Number of dependents -->
        <AggregatorEditRow :complete="isNumberOfDependentsComplete" title="People financially dependent on you" :preview="`${numberOfDependents}`">
          <div class="w-auto mx-auto lg:w-7/12">
            <p class="font-semibold label">How many people are financially dependent on you?</p>
            <!-- 0 Dependents -->
            <ValidationProvider name="Number of dependents" ref="numberOfDependents" rules="required">
              <div slot-scope="{ errors }">
                <div class="flex">
                  <input name="dep-0" v-model.number="numberOfDependents" type="radio" id="dep-0" value="0" />
                  <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="rounded-l-full dependent-label text-l" for="dep-0">
                    0
                    <TickIcon class="tick-icon"/>
                  </label>
                  <!-- 1 Dependents -->
                  <input name="dep-1" v-model.number="numberOfDependents" type="radio" id="dep-1" value="1" />
                  <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="dependent-label text-l" for="dep-1">
                    1
                    <TickIcon class="tick-icon"/>
                  </label>
                  <!-- 2 Dependents -->
                  <input name="dep-2" v-model.number="numberOfDependents" type="radio" id="dep-2" value="2" />
                  <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="dependent-label text-l" for="dep-2">
                    2
                    <TickIcon class="tick-icon"/>
                  </label>
                  <!-- 3 Dependents -->
                  <input name="dep-3" v-model.number="numberOfDependents" type="radio" id="dep-3" value="3" />
                  <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="dependent-label text-l" for="dep-3">
                    3
                    <TickIcon class="tick-icon"/>
                  </label>
                  <!-- 4 Dependents -->
                  <input name="dep-4" v-model.number="numberOfDependents" type="radio" id="dep-4" value="4" />
                  <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="dependent-label text-l" for="dep-4">
                    4
                    <TickIcon class="tick-icon"/>
                  </label>
                  <!-- 5+ Dependents -->
                  <input name="dep-5" v-model.number="numberOfDependents" type="radio" id="dep-5" value="5+" />
                  <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="rounded-r-full dependent-label text-l" for="dep-5">
                    5+
                    <TickIcon class="tick-icon"/>
                  </label>
                </div>
                <!-- Error message -->
                <p class="mt-2 text-polred">
                  {{ errors[0] }}
                </p>
              </div>
            </ValidationProvider>
          </div>
        </AggregatorEditRow>
        
        <!-- Income amount -->
        <AggregatorEditRow :complete="isIncomeAmountComplete" title="Income (per year before tax)" :preview="`${formatAmount(incomeAmount)}`">
          <label class="font-semibold label" for="incomePerYear">
            Income (per year before tax)
            <HelpPopup key="incomePerYear" text="Your income includes your salary, pension and any other benefits you receive."/>
          </label>
          <!-- Income Per Year -->
          <ValidationProvider name="Income per year" ref="incomeAmount" rules="required|numeric">
            <div slot-scope="{ errors }">
              <input tabindex="0" :class="{ 'border-error': errors[0] }" v-model="incomeAmount" :placeholder="'e.g. 15000'" class="input focus:outline-none" id="incomePerYear" type="number" autocomplete="off">
              <p class="text-polred">{{ errors[0] }}</p>
            </div>
          </ValidationProvider>
        </AggregatorEditRow>
        
        <!-- Monthly Outgoings -->
        <AggregatorEditRow :complete="isMonthlyOutgoingsAmountComplete" title="Monthly income spent on mortgages/rent" :preview="`${formatAmount(monthlyOutgoings)}`">
            <label class="font-semibold label" for="monthlyOutgoings">
              How much of your income is spent monthly on mortgages/rent?
              <HelpPopup key="incomePerYear" text="This includes your rent, monthly mortgage payment or a different kind of accommodation cost."/>
            </label>
            <!-- Monthly Outgoings -->
            <ValidationProvider name="Monthly outgoings" ref="monthlyOutgoings" rules="required|numeric">
              <div slot-scope="{ errors }">
                <input tabindex="0" :class="{ 'border-error': errors[0] }" :placeholder="'e.g. 1500'" v-model="monthlyOutgoings" class="input focus:outline-none" id="monthlyOutgoings" type="number" autocomplete="off">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
        </AggregatorEditRow>

        <!-- Additional Income -->
        <AggregatorEditRow :complete="isAdditionalIncomeSectionComplete" title="In addition to your own income, are there other sources of income in your household" :preview="prettifyAdditionalIncome(additionalIncomeIndicator, otherHouseholdIncome)">
            <ValidationProvider name="Additional Income selection" ref="additionalIncomeIndicator">
              <div class="w-full lg:w-8/12 mx-auto" slot-scope="{ errors }">
                <p class="font-semibold label">
                  In addition to your own income, are there other sources of income in your household?
                </p>
                <!-- Additional Income Indicator : Yes -->
                <input v-model.trim="additionalIncomeIndicator" type="radio" name="yesAdditionalIncomeYes" id="yesAdditionalIncomeYes" value="Y" />
                <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" for="yesAdditionalIncomeYes">
                  Yes
                  <TickIcon class="tick-icon"/>
                </label>
                <!-- Additional Income Indicator : No -->
                <input v-model.trim="additionalIncomeIndicator" type="radio" name="yesAdditionalIncomeNo" id="yesAdditionalIncomeNo" value="N" />
                <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" for="yesAdditionalIncomeNo">
                  No
                  <TickIcon class="tick-icon"/>
                </label>
                <div v-if="additionalIncomeIndicator == 'Y'" class="mt-6">
                  <!-- Additional Income amount -->
                  <label class="font-semibold label" for="otherHouseholdIncome"> What is this additional income per year, before tax? 
                    <HelpPopup key="additionalIncomeIndicator" text="The pre-tax income of anyone living at the address with a financial connection to you (e.g. joint bank accounts, mortgages, credit cards)"/>
                  </label>
                  <ValidationProvider name="What is this additional income per year, before tax? " ref="otherHouseholdIncome" rules="required|numeric">
                    <div slot-scope="{ errors }">
                      <input tabindex="0" v-model.trim.number="otherHouseholdIncome" :placeholder="'e.g. 1500'" :class="{ 'border-error': errors[0] }" class="input focus:outline-none" id="otherHouseholdIncome" type="number" autocomplete="off">
                      <p class="text-polred">{{ errors[0] }}</p>
                    </div>
                  </ValidationProvider>
                </div>
                <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
        </AggregatorEditRow>

        <!-- Interested in taking an immediate cash advance -->
        <AggregatorEditRow :complete="['Y', 'N', 'U'].includes(cashAdvanceIndicator)" :required="false" title="Interested in taking an immediate cash advance" :preview="prettyifyCashAdvanceIndicatorString(cashAdvanceIndicator)">
            <ValidationProvider ref="cashAdvanceIndicator" name="Cash Advance selection">
              <div class="mx-auto lg:w-6/12" slot-scope="{ errors }">
              <fieldset>
                <legend class="font-semibold label">
                  Are you interested in taking a cash advance?
                  <HelpPopup key="cashAdvanceIndicator" text="Cash advance allows you to immediately make cash withdrawals from an ATM."/>
                </legend>
                <div class="flex">
                    <label class="mr-4 checkbox-label">
                      <div tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0], 'bg-polgrey-dark border-polgrey-dark' : cashAdvanceIndicator == 'Y'}" class="flex inline-flex items-center justify-center w-6 h-6 p-1 mr-4 text-lg bg-white border rounded cursor-pointer border-polgrey-medium">
                        <input name="cashAdvanceIndicator" :aria-checked="`${cashAdvanceIndicator == 'Y'}`" value="Y" v-model="cashAdvanceIndicator" true-value="Y" false-value="N" type="radio" class="hidden">
                        <TickIcon class="hidden w-6 h-6 pointer-events-none fill-current"/>
                      </div>
                      <div class="relative inline mr-2 select-none check-label"> Yes</div>
                    </label>
                    <label class="checkbox-label">
                      <div tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0], 'bg-polgrey-dark border-polgrey-dark' : cashAdvanceIndicator == 'N'}" class="flex inline-flex items-center justify-center w-6 h-6 p-1 mr-4 text-lg bg-white border rounded cursor-pointer border-polgrey-medium">
                        <input name="cashAdvanceIndicator" value="N" :aria-checked="`${cashAdvanceIndicator == 'N'}`" v-model="cashAdvanceIndicator" true-value="Y" false-value="N" type="radio" class="hidden">
                        <TickIcon class="hidden w-6 h-6 pointer-events-none fill-current"/>
                      </div>
                      <div class="relative inline mr-2 select-none check-label"> No</div>
                    </label>
                </div>
                <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
                </fieldset>
              </div>
          </ValidationProvider>
        </AggregatorEditRow>

        <!-- CreditType -->
        <AggregatorEditRow :complete="isCreditTypeSectionComplete" :creditTypeFlag="creditType" title="Are you interested in transferring a balance?" :preview="prettifyCreditType(creditType, creditAmount)">
            <ValidationProvider name="Credit Type" ref="creditType">
              <div class="w-full lg:w-8/12 mx-auto" slot-scope="{ errors }">
                <p class="font-semibold label">
                  Are you interested in transferring a balance?
                </p>
                <!-- Credit Type : Yes -->
                <input v-model.trim="creditType" type="radio" name="yesCreditTypeYes" id="yesCreditTypeYes" value="BT" />
                <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" for="yesCreditTypeYes">
                  Yes
                  <TickIcon class="tick-icon"/>
                </label>
                <!-- Credit Type : No -->
                <input v-model.trim="creditType" type="radio" name="yesCreditTypeNo" id="yesCreditTypeNo" value="N" />
                <label tabindex="0" @keypress.enter.prevent="$event.target.click" :class="{ 'border-error': errors[0] }" class="text-xl border border-polgrey-medium label-select" for="yesCreditTypeNo">
                  No 
                  <TickIcon class="tick-icon"/>
                </label>
                <div v-if="creditType == 'BT'" class="mt-6">
                  <!-- Credit Amount -->
                  <label class="font-semibold label" for="creditAmount"> How much do you want to transfer? (£)
                  </label>
                  <ValidationProvider name="Balance Transfer amount" ref="creditAmount" :placeholder="placeholder" rules="required|integer|min_value:50|max:7">
                    <div slot-scope="{ errors }">
                      <input tabindex="0" v-model.trim.number="creditAmount" :placeholder="'e.g. 1500'" :class="{ 'border-error': errors[0] }" class="input focus:outline-none" id="creditAmount" type="number" autocomplete="off">
                      <p class="text-polred">{{ errors[0] }}</p>
                    </div>
                  </ValidationProvider>
                </div>
                <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
        </AggregatorEditRow>

      </div>
    </ValidationObserver>
  </div>
</template>

<script>

import TickIcon from '@/v2/assets/images/icons/select-tick.svg'
import mixins from '@/v2/mixins'

import AggregatorEditRow from '@/v2/components/elements/AggregatorFlow/EditRow'
import HelpPopup from '@/v2/components/elements/HelpPopup'

import { mapFields } from 'vuex-map-fields'
import { employmentStatusCodes } from  '../../../form'

export default {
  name: 'WorkAndFinances',
  data() {
    return {
      employmentStatusCodes,
      isEmploymentStatusComplete: false,
      isNumberOfDependentsComplete: false,
      isIncomeAmountComplete: false,
      isMonthlyOutgoingsAmountComplete: false,
      isIncomeReductionSectionComplete: false,
      isAdditionalIncomeSectionComplete: false,
      isCreditTypeSectionComplete: false,
      isCashAdvanceIndicatorComplete: false,
      isOccupationFieldComplete: false,
      occupationRules: ''
    }
  },
  components: {
    TickIcon,
    AggregatorEditRow,
    HelpPopup
  },
  mixins: [mixins],
  computed : {
    ...mapFields([
      'person.employmentStatusCode',
      'person.additionalIncomeIndicator',
      'person.incomeAmount',
      'person.numberOfDependents',
      'person.occupation',
      'person.monthlyOutgoings',
      'person.cashAdvanceIndicator',
      'person.otherHouseholdIncome',
      'creditRequested.creditType',
      'creditRequested.creditAmount',
    ]),
    isSectionComplete() {
      return this.isEmploymentStatusComplete,
        this.isNumberOfDependentsComplete,
        this.isIncomeAmountComplete,
        this.isMonthlyOutgoingsAmountComplete,
        this.isAdditionalIncomeSectionComplete,
        this.isCreditTypeSectionComplete,
        this.isCashAdvanceIndicatorComplete,
        this.isOccupationFieldComplete,
        Date.now()
    },
    employmentStatusField() {
      return this.employmentStatusCode, Date.now()
    },
    numberOfDependentsField() {
      return this.numberOfDependents, Date.now()
    },
    incomeAmountField() {
      return this.incomeAmount, Date.now()
    },
    monthlyOutgoingsAmountField() {
      return this.monthlyOutgoings, Date.now()
    },
    additionalIncomeSection() {
      return this.additionalIncomeIndicator, this.otherHouseholdIncome, Date.now()
    },
    creditTypeSection() {
      return this.creditType, this.creditAmount, Date.now()
    },
    cashAdvanceIndicatorField() {
      return this.cashAdvanceIndicator, Date.now()
    },
    occupationField() {
      return this.occupation, Date.now()
    },
  },
  watch: {
    isSectionComplete: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          await this.$root.$emit(`workAndFinancesComplete`, this.isEmploymentStatusComplete &&
            this.isNumberOfDependentsComplete &&
            this.isIncomeAmountComplete &&
            this.isMonthlyOutgoingsAmountComplete &&
            this.isAdditionalIncomeSectionComplete &&
            this.isOccupationFieldComplete &&
            this.isCreditTypeSectionComplete)
        })
      },
    },
    occupationField: {
      immediate: true,
      async handler() {

        this.occupationRules = this.occupation.length ? 'required|min:1|max:32' : ''

        if (this.occupation.length > 0) {
          this.occupationRules = 'required|min:1|max:32'
          this.isOccupationFieldComplete = await this.validateField(this.$refs, 'occupation')
        } else {
          this.isOccupationFieldComplete = true
        }
      }
    },
    employmentStatusField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isEmploymentStatusComplete = await this.validateField(this.$refs, 'employmentStatus')
        })
      }
    },
    numberOfDependentsField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isNumberOfDependentsComplete = await this.validateField(this.$refs, 'numberOfDependents')
        })
      }
    },
    incomeAmountField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isIncomeAmountComplete = await this.validateField(this.$refs, 'incomeAmount')
        })
      }
    },
    monthlyOutgoingsAmountField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isMonthlyOutgoingsAmountComplete = await this.validateField(this.$refs, 'monthlyOutgoings')
        })
      }
    },
    additionalIncomeSection: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {

          if (this.additionalIncomeIndicator == '' && this.otherHouseholdIncome != '' && this.otherHouseholdIncome >= 1) {
            this.additionalIncomeIndicator = 'Y'
          }

          if (this.additionalIncomeIndicator == 'Y') {
            this.isAdditionalIncomeSectionComplete = (
              await this.validateField(this.$refs, 'additionalIncomeIndicator') &&
              await this.validateField(this.$refs, 'otherHouseholdIncome')
            )
            return
          }

          this.isAdditionalIncomeSectionComplete = await this.validateField(this.$refs, 'additionalIncomeIndicator')
        })
      }
    },
    creditTypeSection: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {

          if (this.creditType == '' && this.creditAmount != '' && this.creditAmount >= 1) {
            this.creditType = 'BT'
          }

          if (this.creditType == 'BT') {
            this.isCreditTypeSectionComplete = (
              await this.validateField(this.$refs, 'creditType') &&
              await this.validateField(this.$refs, 'creditAmount')
            )
            return
          }

          this.isCreditTypeSectionComplete = await this.validateField(this.$refs, 'creditType')
        })
      }
    },
    cashAdvanceIndicatorField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {

          if (!this.cashAdvanceIndicator || this.cashAdvanceIndicator == 'U') {
            this.isCashAdvanceIndicatorComplete = true
          }

          this.isCashAdvanceIndicatorComplete = await this.validateField(this.$refs, 'cashAdvanceIndicator')
        })
      }
    },
  }
}
</script>

<style scoped>

</style>
